import { IPendingSection1Repository } from '../../application/ports/outbound/IPendingSection1Repository';
import { PendingSection1Result } from '../../application/domain/entities/PendingSection1Result';
import { SortOrder } from "@/shared/application/domain/enums/SortOrder";
import { PendingSection1RequestDto } from './dto/req/PendingSection1RequestDto';
import { PendingSection1ResponseDto } from './dto/res/PendingSection1ResponseDto';
import { mapPendingSection1ItemDtoToEntity } from './mappers/pendingSection1ItemMapper';
import { HttpClient } from '@/shared/infra/http/HttpClient';
import { I9ListingFilters } from '@/shared/application/domain/entities/I9ListingFilters';

export class ApiPendingSection1Repository implements IPendingSection1Repository {
  constructor(private readonly httpClient: HttpClient) { }

  async fetchPendingSection1Records(
    page: number,
    pageSize: number,
    columnName?: string,
    sortOrder?: SortOrder,
    filters?: I9ListingFilters
  ): Promise<PendingSection1Result> {
    try {
      const request: PendingSection1RequestDto = {
        page,
        pageSize,
        columnName,
        sortOrder,
        ...filters,
      };

      const queryParams = new URLSearchParams({
        page: request.page.toString(),
        pageSize: request.pageSize.toString(),
      });
      if (request.columnName) {
        queryParams.append('columnName', request.columnName);
      }
      if (request.sortOrder) {
        queryParams.append('sortOrder', request.sortOrder);
      }
      // Only non-empty filters are sent: the backend validates `locationId` as a UUID and `hireDate` as
      // a date, so an empty value would be a 400 rather than "no filter".
      if (request.employeeFirstName) {
        queryParams.append('employeeFirstName', request.employeeFirstName);
      }
      if (request.employeeLastName) {
        queryParams.append('employeeLastName', request.employeeLastName);
      }
      if (request.locationId) {
        queryParams.append('locationId', request.locationId);
      }
      if (request.hireDate) {
        queryParams.append('hireDate', request.hireDate);
      }

      const url = `/dashboard/section1/pending?${queryParams.toString()}`;
      const response = await this.httpClient.getPaginated<PendingSection1ResponseDto>(url);

      const pagination = response.pagination || {};
      return {
        items: Array.isArray(response.data)
          ? response.data.map(mapPendingSection1ItemDtoToEntity)
          : [],
        totalPages: pagination.totalPages ?? 1,
        totalItems: pagination.totalItems ?? 0,
        currentPage: pagination.currentPage ?? 1,
      };
    } catch (error) {
      console.error('Error fetching pending section 1 items:', error);
      throw error;
    }
  }

  /**
   * Send a pending Section 1 reminder email.
   * Backend handles the email template and content.
   * @param i9FormId - The I9Form ID to send reminder for
   * @param email - The recipient email address
   * @returns success + onCooldown (true when the reminder was suppressed by the cooldown window)
   */
  async sendEmailReminder(
    i9FormId: string,
    email: string
  ): Promise<{ success: boolean; onCooldown: boolean }> {
    try {
      const url = '/i9Form/email-notifications/pending-section1';
      const response = await this.httpClient.post<{ success: boolean; onCooldown: boolean }>(url, {
        i9FormId,
        email,
      });
      return response.data;
    } catch (error) {
      console.error('Error sending email reminder:', error);
      throw error;
    }
  }

  /**
   * Re-send the Section 1 AMENDMENT email with a fresh capability-token link (amendment rows).
   * The backend re-mints the token and re-sends; there is no cooldown on this path.
   */
  async resendAmendment(
    i9FormId: string,
    email: string
  ): Promise<{ success: boolean; onCooldown: boolean }> {
    try {
      const response = await this.httpClient.post<{ success: boolean }>(
        '/amendment/section1/resend',
        { i9FormId, email }
      );
      return { success: response.data?.success ?? false, onCooldown: false };
    } catch (error) {
      console.error('Error resending amendment link:', error);
      throw error;
    }
  }
}
